import { InfiniteData, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { LAMBDA_FORMS } from './constants';
import authenticatedApi from './authenticatedApi';
import { Form } from '../pages/FormList/FormList';
import { useEffect, useMemo, useState } from 'react';
import {
  GridFilterItem,
  GridInputRowSelectionModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid';

const BULK_TIMEOUT_MS = 30_000;

const hasSelectedForms = (
  data:
    | InfiniteData<
        {
          forms: Form[];
          lastEvaluatedKey: any;
        },
        unknown
      >
    | undefined,
  selectedFormIds: GridInputRowSelectionModel[]
): boolean => {
  if (!data || selectedFormIds.length === 0) return false;

  const allForms = data.pages.flatMap((page: any) => page.forms);
  return allForms.some((f: any) => selectedFormIds.includes(f.id));
};

export const getForms = async (
  lastEvaluatedKey = null,
  filter: GridFilterItem | null
): Promise<{ forms: Form[]; lastEvaluatedKey: any }> => {
  const params = {
    lastEvaluatedKey: lastEvaluatedKey ? JSON.stringify(lastEvaluatedKey) : undefined,
    ...(filter?.field && filter.value && { [filter.field]: filter.value }),
  };

  const queryString = (
    Object.keys(params) as ('lastEvaluatedKey' | 'userEmail' | 'formNumber' | 'formTitle')[]
  )
    .map((key) =>
      params[key] ? `${encodeURIComponent(key)}=${encodeURIComponent(params?.[key] || '')}` : ''
    )
    .filter(Boolean)
    .join('&');

  const records: any = await authenticatedApi.get(`${LAMBDA_FORMS}/forms?${queryString}`);

  const forms: Form[] = records.data.map((record: any) => {
    const form: Form = {
      formNumber: record.metadata.form_number,
      formTitle: record.metadata.form_title,
      status: record.status,
      dateModified: record.timestamp,
      id: record.id,
      user: record.user,
      userEmail: record.user.email,
      origin: record.origin,
    };
    return form;
  });

  return {
    forms,
    lastEvaluatedKey: records.lastEvaluatedKey,
  };
};

export const useGetForms = (
  selectedFormIds: GridRowSelectionModel,
  setSelectedFormIds: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>,
  bulkActionInProgress: boolean,
  setBulkActionInProgress: React.Dispatch<React.SetStateAction<boolean>>,
  filter: GridFilterItem | null
) => {
  const [bulkTimedOutIds, setBulkTimedOutIds] = useState<string[] | null>(null);

  //selected forms state is composite key, so just grab the ID
  const normalizeSelectedFormIds = useMemo(
    () => selectedFormIds.map((id) => id.toString().split('#')[0]),
    [selectedFormIds]
  );

  const query = useInfiniteQuery({
    queryKey: ['forms', filter],
    queryFn: ({ pageParam = null }) => getForms(pageParam, filter),
    getNextPageParam: (lastPage) => {
      return lastPage.lastEvaluatedKey || undefined;
    },
    placeholderData: keepPreviousData,
    refetchInterval: (q) => {
      if (!bulkActionInProgress) return false;
      if (!normalizeSelectedFormIds.length) return false;

      const data = q.state.data;
      const stillThere = hasSelectedForms(data, normalizeSelectedFormIds);

      if (!stillThere) return false;

      return 5000;
    },
    initialPageParam: null,
    staleTime: 30000,
    retry: false,
  });

  useEffect(() => {
    if (!bulkActionInProgress) return;
    if (!normalizeSelectedFormIds.length) return;
    if (!query.data) return;

    const stillThere = hasSelectedForms(query.data, normalizeSelectedFormIds);

    //all forms finished, no errors
    if (!stillThere) {
      setSelectedFormIds([]);
      setBulkActionInProgress(false);
      setBulkTimedOutIds(null);
    }
  }, [
    bulkActionInProgress,
    normalizeSelectedFormIds,
    query.data,
    setSelectedFormIds,
    setBulkActionInProgress,
  ]);

  useEffect(() => {
    if (!bulkActionInProgress) return;
    if (!normalizeSelectedFormIds.length) return;

    //timeout after 30s
    const timeoutId = setTimeout(() => {
      setBulkTimedOutIds(normalizeSelectedFormIds);
      setBulkActionInProgress(false);
    }, BULK_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [bulkActionInProgress, normalizeSelectedFormIds, setBulkActionInProgress]);

  return { ...query, bulkTimedOutIds, setBulkTimedOutIds };
};
